import { odooApiFetch } from "app/utils/auth";
import { DeliveryDashboardSettings } from "app/types/deliveryDashboardSettings";

export const DELIVERY_DASHBOARD_SETTINGS_UPDATED_EVENT =
  "dyn_delivery_dashboard_settings_updated";

let cachedSettings: DeliveryDashboardSettings | null = null;

export function getCachedDeliveryDashboardSettings(): DeliveryDashboardSettings | null {
  return cachedSettings;
}

export function getGoogleMapsApiKeyFromSettings(
  settings: DeliveryDashboardSettings | null | undefined
): string | null {
  const key = settings?.google_maps?.google_maps_api_key;
  if (typeof key !== "string") {
    return null;
  }
  const trimmed = key.trim();
  return trimmed || null;
}

export function isGoogleMapsConfigured(
  settings: DeliveryDashboardSettings | null | undefined
): boolean {
  if (settings?.google_maps?.configured === true) {
    return true;
  }
  return Boolean(getGoogleMapsApiKeyFromSettings(settings));
}

export function subscribeToDeliveryDashboardSettings(
  listener: (settings: DeliveryDashboardSettings | null) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<DeliveryDashboardSettings | null>;
    listener(customEvent.detail ?? null);
  };

  window.addEventListener(DELIVERY_DASHBOARD_SETTINGS_UPDATED_EVENT, handler);
  return () => {
    window.removeEventListener(DELIVERY_DASHBOARD_SETTINGS_UPDATED_EVENT, handler);
  };
}

function publishSettings(settings: DeliveryDashboardSettings | null): void {
  cachedSettings = settings;
  window.dispatchEvent(
    new CustomEvent(DELIVERY_DASHBOARD_SETTINGS_UPDATED_EVENT, {
      detail: settings,
    })
  );
}

export function clearDeliveryDashboardSettings(): void {
  publishSettings(null);
}

/** Single settings endpoint used after login; do not add per-feature settings calls. */
export async function fetchDeliveryDashboardSettings(
  accessToken: string
): Promise<DeliveryDashboardSettings> {
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  if (!apiBaseUrl) {
    throw new Error("Missing REACT_APP_API_BASE_URL.");
  }

  const settings = await odooApiFetch<DeliveryDashboardSettings>(
    `${apiBaseUrl}/api/delivery.dashboard/settings`,
    accessToken
  );

  publishSettings(settings);
  return settings;
}

export async function syncDeliveryDashboardSettings(
  accessToken: string
): Promise<DeliveryDashboardSettings | null> {
  try {
    return await fetchDeliveryDashboardSettings(accessToken);
  } catch (error) {
    console.error("Failed to load delivery dashboard settings:", error);
    publishSettings(null);
    return null;
  }
}
